import { useState } from 'react';
import { Link } from 'react-router';
import type { SetupStep } from '../lib/types';
import { Section, Sq } from './ui';

// First-run checklist on the Overview. The server says which steps are done (from real data,
// demo records never count); this file owns the wording and where each step is done.
const STEPS: { key: SetupStep; title: string; why: string; to: string; action: string }[] = [
  {
    key: 'worker',
    title: 'Worker running',
    why: 'The worker collects websites, reads quotations and compiles reports. Jobs wait in the queue until it reports in; if this stays open, ask whoever runs the server to start it.',
    to: '/jobs',
    action: 'See the worker',
  },
  {
    key: 'studios',
    title: 'Add your first competitors',
    why: 'Add a studio by its website, or import a CSV of the studios you already track.',
    to: '/competitors',
    action: 'Add studios',
  },
  {
    key: 'review',
    title: 'Sort a studio by interest',
    why: 'Mark studios Interesting, Watch later or Dismissed so the list reflects what matters to you.',
    to: '/competitors?interest=unreviewed&demo=hide',
    action: 'Review studios',
  },
  {
    key: 'deep',
    title: 'Follow one studio closely',
    why: 'Promote a studio to Deep: its website is collected on a schedule and changes are flagged for you.',
    to: '/competitors',
    action: 'Choose a studio',
  },
  {
    key: 'quotation',
    title: 'Upload a quotation',
    why: 'Drop a PDF quotation. You check every extracted price before it counts in a comparison.',
    to: '/pricing',
    action: 'Open Pricing',
  },
  {
    key: 'brand',
    title: 'Add the Mirage logo to the brand kit',
    why: 'Posters and carousels made in the Content Studio place it for you.',
    to: '/content?tab=brand',
    action: 'Open the brand kit',
  },
  {
    key: 'prospect',
    title: 'Add a first prospect',
    why: 'An architect, developer or agency you would like to work with. Research and outreach drafts start from there.',
    to: '/prospects',
    action: 'Add a prospect',
  },
];

const HIDE_KEY = 'mm.getStarted.hidden';

function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHidden(v: boolean) {
  try {
    if (v) localStorage.setItem(HIDE_KEY, '1');
    else localStorage.removeItem(HIDE_KEY);
  } catch {
    /* storage blocked: the choice lasts for this page view only */
  }
}

/** Hidden once every step is done. Hiding it is remembered in this browser only. */
export function useGetStarted(state: Record<SetupStep, boolean>) {
  const [hidden, setHidden] = useState(readHidden);
  const done = STEPS.filter((s) => state[s.key]).length;
  const complete = done === STEPS.length;
  return {
    done,
    total: STEPS.length,
    visible: !complete && !hidden,
    canReopen: !complete && hidden,
    hide: () => {
      writeHidden(true);
      setHidden(true);
    },
    show: () => {
      writeHidden(false);
      setHidden(false);
    },
  };
}

export function GetStarted({ state, onHide }: { state: Record<SetupStep, boolean>; onHide: () => void }) {
  const done = STEPS.filter((s) => state[s.key]).length;
  const next = STEPS.find((s) => !state[s.key]);
  return (
    <Section
      title="Get started"
      id="get-started"
      aside={
        <span className="row gap-s">
          <span className="muted num">
            {done} of {STEPS.length} done
          </span>
          <button type="button" className="link" onClick={onHide}>
            Hide
          </button>
        </span>
      }
    >
      <ol className="setup-steps">
        {STEPS.map((s) => {
          const isDone = state[s.key];
          const isNext = s === next;
          return (
            <li key={s.key} className={isDone ? 'done' : isNext ? 'next' : undefined}>
              <Sq kind={isDone ? 'filled' : 'outline'} tone={isDone ? 'ok' : undefined} label={isDone ? 'Done' : 'To do'} />
              <div className="setup-text">
                <strong>{s.title}</strong>
                {!isDone && <span className="muted small">{s.why}</span>}
              </div>
              {isDone ? (
                <span className="muted small">Done</span>
              ) : (
                <Link className={`btn small${isNext ? ' primary' : ''}`} to={s.to}>
                  {s.action}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </Section>
  );
}
